/**
 * Chart-symbology themes — a selectable symbol SET, the same idea as the chart
 * PROVIDER selector but for how vector features are drawn rather than which
 * chart supplies them.
 *
 *  - 'current'  our existing simplified, ECDIS-leaning rendering.
 *  - 'np5011'   the Admiralty paper-chart look (INT1 traditional symbols).
 *  - 'np5012'   the Admiralty ENC / ECDIS look (S-52 presentation-library style).
 *
 * INT1 and S-52 are international standards, so we draw OUR OWN renderings of the
 * standard symbols. We do NOT copy UKHO's NP5011 / NP5012 publication artwork or
 * their proprietary glyph files; the names here just say which standard look a
 * set is aiming at.
 *
 * Each set maps to a `glyphStyle` the canvas symbol generator understands, plus
 * (later) area/line colour conventions. Keep this list the single source of
 * truth so the store, the selector UI, and the renderer all agree.
 */
import type { SymbolSetId } from '@/store';

/** How the canvas symbol generator should draw the point glyphs (buoys etc.). */
export type GlyphStyle = 'current' | 'paper' | 'ecdis';

export interface SymbolSet {
  id: SymbolSetId;
  /** Short label for the selector. */
  name: string;
  /** One-line description for the selector. */
  blurb: string;
  /** Which glyph rendering the canvas generator uses for this set. */
  glyphStyle: GlyphStyle;
}

export const SYMBOL_SETS: SymbolSet[] = [
  {
    id: 'current',
    name: 'Current',
    blurb: 'Our simplified symbols, the ones we draw now.',
    glyphStyle: 'current',
  },
  {
    id: 'np5011',
    name: 'Paper (INT1)',
    blurb: 'The traditional paper-chart look, INT1 symbols.',
    glyphStyle: 'paper',
  },
  {
    id: 'np5012',
    name: 'ECDIS (S-52)',
    blurb: 'The ENC / ECDIS look, S-52 simplified symbols.',
    glyphStyle: 'ecdis',
  },
];

const DEFAULT = SYMBOL_SETS[0];

/** The set for an id, falling back to the current set if it's unknown. */
export function getSymbolSet(id: SymbolSetId): SymbolSet {
  return SYMBOL_SETS.find((s) => s.id === id) ?? DEFAULT;
}

/** The glyph style for a set id (what the symbol generator keys off). */
export function glyphStyleFor(id: SymbolSetId): GlyphStyle {
  return getSymbolSet(id).glyphStyle;
}
